import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { debounceTime, filter } from 'rxjs';

import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { AvAvatarComponent } from '../../../../../Shared/Components/av-avatar/av-avatar.component';
import { AvContentContainerComponent } from '../../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvEmptyStateComponent } from '../../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvRailComponent } from '../../../../../Shared/Components/av-rail/av-rail.component';
import { AvReferenceComponent } from '../../../../../Shared/Components/av-reference/av-reference.component';
import { AvSkeletonComponent } from '../../../../../Shared/Components/av-skeleton/av-skeleton.component';
import { AvSpinComponent } from '../../../../../Shared/Components/av-spin/av-spin.component';
import { AvAppIconDirective } from '../../../../../Shared/Directives/av-app-icon.directive';
import { AvPendingDirective } from '../../../../../Shared/Directives/av-pending.directive';
import { ThemeService } from '../../../../../Shared/Services/theme.service';
import { waitState } from '../../../../../Shared/Services/wait-state.util';
import { SEARCH_DEBOUNCE_MS } from '../../../../../Catalog/Consts/search-debounce.const';
import { DEMANDS_SEARCH_MAX_LENGTH } from '../../../../../Catalog/Consts/search-max-length.const';
import { DemandStatusChipComponent } from '../../../../../Catalog/Components/Partials/demand-status-chip/demand-status-chip.component';
import {
  ALL_DEMANDS_DISPLAYED_COLUMNS,
  ALL_DEMANDS_SKELETON_COLUMN_WIDTHS,
  REPORT_TAB_LABEL_KEY_MAP,
} from '../../../Consts/all-demands-display.const';
import { AllDemandsFacade } from '../../../Facades/all-demands.facade';
import { AllDemandsDisplayRow } from '../../../Interfaces/all-demands-display-row.interface';
import { AllDemandsFilterCriteria } from '../../../Interfaces/all-demands-filters.interface';
import { ReportSubjectTab } from '../../../Types/report-subject-tab.type';
import { toAllDemandsDisplayRow } from '../../../Shared/all-demands-display-row.util';
import { AllDemandsFilterPanelComponent } from '../../Partials/all-demands-filter-panel/all-demands-filter-panel.component';
import { ReportExportStatusComponent } from '../../Partials/report-export-status/report-export-status.component';

@Component({
  selector: 'app-all-demands',
  standalone: true,
  imports: [
    COMMON_IMPORTS,
    RouterLink,
    AvContentContainerComponent,
    AvAvatarComponent,
    AvEmptyStateComponent,
    AvReferenceComponent,
    AvSkeletonComponent,
    AvRailComponent,
    AvSpinComponent,
    AvPendingDirective,
    AvAppIconDirective,
    DemandStatusChipComponent,
    AllDemandsFilterPanelComponent,
    ReportExportStatusComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './all-demands.component.html',
  styleUrl: './all-demands.component.scss',
})
export class AllDemandsComponent {
  readonly facade = inject(AllDemandsFacade);
  private readonly _themeService = inject(ThemeService);
  private readonly _fb = inject(FormBuilder);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  readonly searchControl = this._fb.nonNullable.control('');
  readonly searchMaxLength = DEMANDS_SEARCH_MAX_LENGTH;
  readonly displayedColumns = ALL_DEMANDS_DISPLAYED_COLUMNS;
  readonly skeletonColumnWidths = ALL_DEMANDS_SKELETON_COLUMN_WIDTHS;

  private readonly _coldLoadSettled = signal(false);

  readonly activeTabIndex = computed(() => {
    const index = this.facade.permittedTabs().indexOf(this.facade.activeTab() ?? 'application');
    return Math.max(0, index);
  });

  readonly rows = computed<AllDemandsDisplayRow[]>(() => {
    const isDark = this._themeService.mode() === 'dark';
    return this.facade.rows().map((row) => toAllDemandsDisplayRow(row, isDark));
  });

  readonly showEmpty = computed(
    () => !this.facade.coldLoading() && this.facade.rows().length === 0,
  );

  readonly emptyIsFiltered = computed(() => this.facade.hasFilters() || this.facade.hasSearch());

  readonly showInitialSpinner = computed(
    () => !this._coldLoadSettled() && this.facade.coldLoading() && this.facade.rows().length === 0,
  );

  readonly coldWait = waitState(this.showInitialSpinner, { once: true });

  readonly showSkeleton = computed(() => this.showInitialSpinner() || this.coldWait.visible());

  readonly refreshWait = waitState(
    computed(
      () => this.facade.loading() && (this.facade.rows().length > 0 || this._coldLoadSettled()),
    ),
  );

  constructor() {
    this._initialiseFromUrl();
    this._registerQueryParamSync();
    this._bindSearchInput();
    this._latchColdLoad();
    this._destroyRef.onDestroy(() => this.facade.leave());
  }

  private _initialiseFromUrl(): void {
    this.facade.initialise(this._route.snapshot.queryParamMap);
    this.searchControl.setValue(this.facade.filters().search, { emitEvent: false });
  }

  private _registerQueryParamSync(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.facade.syncFromQueryParams(this._route.snapshot.queryParamMap)) {
          this.searchControl.setValue(this.facade.filters().search, { emitEvent: false });
        }
      });
  }

  private _latchColdLoad(): void {
    let started = false;

    effect(() => {
      if (this.facade.loading()) {
        started = true;
        return;
      }

      if (started) {
        this._coldLoadSettled.set(true);
      }
    });
  }

  private _bindSearchInput(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        filter((value) => value === this.searchControl.value),
        takeUntilDestroyed(),
      )
      .subscribe((value) => this.facade.setSearch(value));
  }

  tabLabelKey(tab: ReportSubjectTab): string {
    return REPORT_TAB_LABEL_KEY_MAP[tab];
  }

  onSubjectTabChange(event: MatTabChangeEvent): void {
    const tab = this.facade.permittedTabs()[event.index];
    if (tab) this.facade.setSubjectTab(tab);
  }

  onCriteriaChange(partial: Partial<AllDemandsFilterCriteria>): void {
    this.facade.setCriteria(partial);
  }

  onClearFilters(): void {
    this.facade.clearFilters();
  }

  onClearAllCriteria(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.facade.clearAllCriteria();
  }

  onClearSearch(): void {
    this.searchControl.setValue('');
  }

  onSortChange(sort: Sort): void {
    this.facade.setSort(sort.active, sort.direction);
  }

  onPage(event: PageEvent): void {
    this.facade.setPage(event.pageIndex);
  }

  onStartExport(): void {
    this.facade.startExport(this.facade.activeTab() ?? 'application');
  }

  trackRow(_index: number, row: AllDemandsDisplayRow): string {
    return row.id;
  }
}
